import type { TFunction } from "i18next";
import type { LDFlagSet } from "launchdarkly-js-client-sdk";
import { generatePath } from "react-router";

import { Connector } from "shared/api/connections/api";
import { DataFeed } from "shared/api/dataFeeds/api";
import { Schedule } from "shared/api/schedules/types";
import { toTitleCase } from "shared/utils";

import {
  CONNECTIONS_PAGE_CONFIG_KEY,
  PagesConfigModel,
} from "pages/SuperAdmin/PagesConfig/types";

import { getScheduleSummary } from "features/ui/ScheduleCadenceEditor/summary";

import { routes } from "services/routes";

import {
  CONNECTOR_CREDENTIAL_FIELDS,
  CONNECTOR_NEW_TITLE_KEYS,
  CONNECTOR_SETTING_FIELDS,
} from "./constants";
import { CredentialFieldDefinition, SettingFieldDefinition } from "./types";

export const getCredentialFields = (
  connectorID: string
): CredentialFieldDefinition[] =>
  CONNECTOR_CREDENTIAL_FIELDS[connectorID] ?? [];

export const getSettingFields = (
  connectorID: string
): SettingFieldDefinition[] => CONNECTOR_SETTING_FIELDS[connectorID] ?? [];

export const getNewConnectionTitle = (
  t: TFunction,
  { ID, name }: Connector
): string =>
  t(CONNECTOR_NEW_TITLE_KEYS[ID] ?? "superadmin:connections.detail.new_title", {
    connector: name,
  });

// A connector that lists nothing to type cannot be set up at all, rather than set up with nothing: its card would
// open a form that can never be submitted.
export const canSetUpConnector = (connectorID: string): boolean =>
  getCredentialFields(connectorID).length +
    getSettingFields(connectorID).length >
  0;

// A saved connection names its connector by id. The registry supplies the display name; a connector it no longer
// lists falls back to a readable form of the id rather than hiding the connection.
export const getConnector = (
  connectors: Connector[] | undefined,
  type: string
): Connector =>
  connectors?.find(({ ID }) => ID === type) ?? {
    ID: type,
    name: toTitleCase(type),
    tags: [],
  };

export const getFeedScheduleText = (
  t: TFunction,
  schedule: Pick<Schedule, "schedules" | "timezone"> | null | undefined
): string =>
  (schedule?.timezone &&
    getScheduleSummary(t, schedule.schedules, schedule.timezone)) ??
  t("superadmin:connections.data_feeds.not_scheduled");

export const getDataFeedPath = ({ connectionID, ID }: DataFeed): string =>
  generatePath(routes.dataFeed, { id: connectionID, feedID: ID });

interface ConnectionsGate {
  pages: PagesConfigModel;
  flags: LDFlagSet;
}

// The Connections pages are only there while the tenant has the page on and the connections flag on. Anything
// that leads to them checks the same two things, or it leads to a page that is not there.
export const canUseConnections = ({ pages, flags }: ConnectionsGate): boolean =>
  !!pages[CONNECTIONS_PAGE_CONFIG_KEY] && !!flags.connections;
